import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Autocomplete,
  Card,
  CardContent,
  CardHeader,
  InputAdornment,
  Tooltip,
  CircularProgress,
  styled
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Person as PersonIcon,
  Directions as DirectionsIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateRangeIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  CreditCard as CreditCardIcon,
  Work as WorkIcon,
  Route as RouteIcon,
  Straighten as StraightenIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import PutyovkaYonalish from '../../services/PutyovkaYonalish';
import ShafyorService from '../../services/ShafyorService';
import PrisepsService from '../../services/PrisepsService';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(3),
}));

const SectionTitle = ({ icon: Icon, title }) => (
  <Box display="flex" alignItems="center" mb={2}>
    <Icon color="primary" sx={{ mr: 1 }} />
    <Typography variant="h6" color="primary">
      {title}
    </Typography>
  </Box>
);

const PutyovkaForm = ({ onSubmit, transportId, initialValues }) => {
  const [yonalishList, setYonalishList] = useState([]);
  const [shafyorList, setShafyorList] = useState([]);
  const [prisepsList, setPrisepsList] = useState([]);
  const [loading, setLoading] = useState({
    yonalish: true,
    shafyor: true,
    priseps: true
  });
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShafyor, setSelectedShafyor] = useState(null);
  const [selectedPriseps, setSelectedPriseps] = useState(null);
  const comboRef = useRef(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [yonalishRes, shafyorRes, prisepsRes] = await Promise.all([
          PutyovkaYonalish.getYonalishList(),
          ShafyorService.searchByName(),
          PrisepsService.searchByName()
        ]);
        
        setYonalishList(yonalishRes.data);
          console.log('shafyors effectdagi', shafyorRes.data);
        setShafyorList(shafyorRes.data);
        setPrisepsList(prisepsRes.data);
      } catch (err) {
        setError('Ma\'lumotlarni yuklab bo\'lmadi');
        console.error('Error fetching data:', err);
      } finally {
        setLoading({
          yonalish: false,
          shafyor: false,
          priseps: false
        });
      }
    };

    fetchInitialData();
  }, []);

  const handleShafyorSearch = async (searchText) => {
    setSearchTerm(searchText);
    if (searchText.length > 2) {
      try {
        const response = await ShafyorService.searchByName(searchText);
        setShafyorList(response.data);
      } catch (err) {
        console.error('Error searching shafyors:', err);
      }
    }
  };

  const handlePrisepsSearch = async (searchText) => {
    if (searchText.length > 2) {
      try {
        const response = await PrisepsService.searchByName(searchText);
        setPrisepsList(response.data);
      } catch (err) {
        console.error('Error searching priseps:', err);
      }
    }
  };

  const handleAddShafyor = (shafyor, push) => {
    if (!shafyor) return;
    
    const newShafyor = {
      id: shafyor.id || Math.random().toString(36).substr(2, 9),
      name: `${shafyor.ism || ''} ${shafyor.familiya || ''}`.trim(),
      phone: shafyor.telefon || '',
      role: 'Haydovchi'
    };
    
    // Check if shafyor already exists
    const exists = initialValues.shafyors.some(s => s.id === newShafyor.id || s.phone === newShafyor.phone);
    if (!exists) {
      push(newShafyor);
    }
  };

  const handleAddPriseps = (priseps, push) => {
    if (priseps) {
      push({
        ega_id: priseps.id,
        name: priseps.name,
        davlat_raqam: priseps.davlat_raqam || '',
        tex_passport_raqam: priseps.tex_passport_raqam || '',
        rang: priseps.rang || '',
        izoh: priseps.izoh || ''
      });
      setSelectedPriseps(null);
    }
  };

  const validationSchema = Yup.object({
    raqam: Yup.string().required('Raqam kiritilishi shart'),
    narx: Yup.number().typeError('Raqam kiriting').required('Narx kiritilishi shart'),
    dan: Yup.date().required('Boshlanish sanasi kiritilishi shart'),
    gacha: Yup.date()
      .required('Tugash sanasi kiritilishi shart')
      .min(Yup.ref('dan'), 'Tugash sanasi boshlanish sanasidan keyin bo\'lishi kerak'),
    shafyors: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Ism kiritilishi shart'),
        role: Yup.string().required('Lavozim kiritilishi shart'),
        prava_raqam: Yup.string().required('Prava raqami kiritilishi shart'),
        phone: Yup.string().required('Telefon raqami kiritilishi shart')
      })
    ),
    priseps: Yup.array().of(
      Yup.object().shape({
        ega_id: Yup.string().required('Ega tanlanishi shart'),
        name: Yup.string().required('Nomi kiritilishi shart'),
        davlat_raqam: Yup.string().required('Davlat raqami kiritilishi shart'),
        tex_passport_raqam: Yup.string().required('Tex pasport raqami kiritilishi shart'),
        rang: Yup.string().required('Rangi kiritilishi shart'),
        izoh: Yup.string()
      })
    ),
    yonalishlar: Yup.array()
      .of(
        Yup.object().shape({
          from_davlat: Yup.string().required('Qayerdan kiritilishi shart'),
          to_davlat: Yup.string().required('Qayerga kiritilishi shart')
        })
      )
      .min(1, 'Kamida bitta yo\'nalish qo\'shilishi kerak')
  });

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  if (loading.yonalish || loading.shafyor || loading.priseps) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!transportId) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="error">Transport ID topilmadi. Iltimos, avval transport yarating.</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={{
        transport_id: transportId,
        raqam: initialValues.raqam || '',
        narx: initialValues.narx || '',
        dan: initialValues.dan || '',
        gacha: initialValues.gacha || '',
        shafyors: initialValues.shafyors || [],
        priseps: initialValues.priseps || [],
        yonalishlar: initialValues.yonalishlar || [],
        izoh: initialValues.izoh || ''
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
        <Form>
          {/* Basic Information */}
          <StyledPaper>
            <SectionTitle icon={DateRangeIcon} title="Asosiy ma'lumotlar" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Field
                  as={TextField}
                  fullWidth
                  name="raqam"
                  label="Putyovka raqami"
                  variant="outlined"
                  required
                  error={touched.raqam && Boolean(errors.raqam)}
                  helperText={touched.raqam && errors.raqam}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCardIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  as={TextField}
                  fullWidth
                  name="narx"
                  label="Narx"
                  type="number"
                  variant="outlined"
                  required
                  error={touched.narx && Boolean(errors.narx)}
                  helperText={touched.narx && errors.narx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  as={TextField}
                  fullWidth
                  name="dan"
                  label="Boshlanish sanasi"
                  type="date"
                  variant="outlined"
                  required
                  error={touched.dan && Boolean(errors.dan)}
                  helperText={touched.dan && errors.dan}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRangeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  as={TextField}
                  fullWidth
                  name="gacha"
                  label="Tugash sanasi"
                  type="date"
                  variant="outlined"
                  required
                  error={touched.gacha && Boolean(errors.gacha)}
                  helperText={(touched.gacha && errors.gacha) || " "}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRangeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  fullWidth
                  name="izoh"
                  label="Izoh"
                  variant="outlined"
                  multiline
                  rows={3}
                  error={touched.izoh && Boolean(errors.izoh)}
                  helperText={touched.izoh && errors.izoh}
                />
              </Grid>
            </Grid>
          </StyledPaper>

          {/* Shafyors Section */}
          <StyledPaper>
            <SectionTitle icon={PersonIcon} title="Haydovchilar" />
            <FieldArray name="shafyors">
              {({ push: pushShafyor, remove: removeShafyor }) => (
                <Box>
                  <Box mb={3}>
                    <Autocomplete
                      options={shafyorList || []}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        return `${option.ism || ''} ${option.familiya || ''} (${option.telefon || ''})`;
                      }}
                      value={selectedShafyor}
                      onChange={(event, newValue) => {
                        setSelectedShafyor(newValue);
                        if (newValue) {
                          handleAddShafyor(newValue, pushShafyor);
                          setSelectedShafyor(null); // Reset after adding
                        }
                      }}
                      onInputChange={(event, newInputValue) => {
                        handleShafyorSearch(newInputValue);
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return false;
                        return option.id === value.id;
                      }}
                      getOptionKey={(option) => option?.id || Math.random().toString(36).substr(2, 9)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Haydovchi qidirish"
                          variant="outlined"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <React.Fragment>
                                {loading.shafyor ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>

                  {values.shafyors && values.shafyors.length > 0 && values.shafyors.map((shafyor, index) => (
                    <Box key={index} mt={2} p={2} component={Paper} variant="outlined">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1">
                          Haydovchi #{index + 1}
                        </Typography>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => removeShafyor(index)}
                          disabled={values.shafyors.length <= 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            name={`shafyors.${index}.name`}
                            label="Ism"
                            variant="outlined"
                            required
                            error={touched.shafyors?.[index]?.name && Boolean(errors.shafyors?.[index]?.name)}
                            helperText={touched.shafyors?.[index]?.name && errors.shafyors?.[index]?.name}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            name={`shafyors.${index}.phone`}
                            label="Telefon raqami"
                            variant="outlined"
                            error={touched.shafyors?.[index]?.phone && Boolean(errors.shafyors?.[index]?.phone)}
                            helperText={touched.shafyors?.[index]?.phone && errors.shafyors?.[index]?.phone}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            name={`shafyors.${index}.role`}
                            label="Lavozim"
                            variant="outlined"
                            select
                            required
                            error={touched.shafyors?.[index]?.role && Boolean(errors.shafyors?.[index]?.role)}
                            helperText={touched.shafyors?.[index]?.role && errors.shafyors?.[index]?.role}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <WorkIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          >
                            <MenuItem value="Haydovchi">Haydovchi</MenuItem>
                            <MenuItem value="Yordamchi">Yordamchi</MenuItem>
                          </Field>
                        </Grid>
                      </Grid>
                        <Grid item xs={12} md={8}>
                              <Field
                                as={TextField}
                                fullWidth
                                name={`yonalishlar.${index}.izoh`}
                                label="Izoh"
                                variant="outlined"
                                multiline
                                rows={2}
                                error={
                                  touched.yonalishlar?.[index]?.izoh &&
                                  Boolean(errors.yonalishlar?.[index]?.izoh)
                                }
                                helperText={
                                  touched.yonalishlar?.[index]?.izoh &&
                                  errors.yonalishlar?.[index]?.izoh
                                }
                              />
                            </Grid>
                    </Box>
                  ))}
                </Box>
              )}
            </FieldArray>
          </StyledPaper>

          {/* Yonalishlar Section */}
          <StyledPaper>
            <SectionTitle icon={RouteIcon} title="Yo'nalishlar" />
            <FieldArray name="yonalishlar">
              {({ push: pushYonalish, remove: removeYonalish }) => (
                <Box>
                  <Box mb={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        pushYonalish({
                          yonalish_id: '',
                          qayerdan: '',
                          qayerga: '',
                          masofa: '',
                          narx: '',
                          izoh: ''
                        })
                      }
                      startIcon={<AddIcon />}
                    >
                      Yangi yo'nalish qo'shish
                    </Button>
                  </Box>

                  {values.yonalishlar.map((yonalish, index) => (
                    <Box key={index} mt={2} p={2} component={Paper} variant="outlined">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1">
                          Yo'nalish #{index + 1}
                        </Typography>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => removeYonalish(index)}
                          disabled={values.yonalishlar.length <= 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            name={`yonalishlar.${index}.yonalish_id`}
                            label="Yo'nalish"
                            select
                            variant="outlined"
                            required
                            error={
                              touched.yonalishlar?.[index]?.yonalish_id &&
                              Boolean(errors.yonalishlar?.[index]?.yonalish_id)
                            }
                            helperText={
                              touched.yonalishlar?.[index]?.yonalish_id &&
                              errors.yonalishlar?.[index]?.yonalish_id
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <RouteIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          >
                            {yonalishList.map((y) => (
                              <MenuItem key={y.id} value={y.id}>
                                {y.nomi}
                              </MenuItem>
                            ))}
                          </Field>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            name={`yonalishlar.${index}.qayerdan`}
                            label="Qayerdan"
                            variant="outlined"
                            required
                            error={
                              touched.yonalishlar?.[index]?.qayerdan &&
                              Boolean(errors.yonalishlar?.[index]?.qayerdan)
                            }
                            helperText={
                              touched.yonalishlar?.[index]?.qayerdan &&
                              errors.yonalishlar?.[index]?.qayerdan
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  {/*<LocationOnIcon color="action" />*/}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            name={`yonalishlar.${index}.qayerga`}
                            label="Qayerga"
                            variant="outlined"
                            required
                            error={
                              touched.yonalishlar?.[index]?.qayerga &&
                              Boolean(errors.yonalishlar?.[index]?.qayerga)
                            }
                            helperText={
                              touched.yonalishlar?.[index]?.qayerga &&
                              errors.yonalishlar?.[index]?.qayerga
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  {/*<LocationOnIcon color="action" />*/}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            name={`yonalishlar.${index}.masofa`}
                            label="Masofa"
                            type="number"
                            variant="outlined"
                            required
                            error={
                              touched.yonalishlar?.[index]?.masofa &&
                              Boolean(errors.yonalishlar?.[index]?.masofa)
                            }
                            helperText={
                              touched.yonalishlar?.[index]?.masofa &&
                              errors.yonalishlar?.[index]?.masofa
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <StraightenIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            name={`yonalishlar.${index}.narx`}
                            label="Narx"
                            type="number"
                            variant="outlined"
                            required
                            error={
                              touched.yonalishlar?.[index]?.narx &&
                              Boolean(errors.yonalishlar?.[index]?.narx)
                            }
                            helperText={
                              touched.yonalishlar?.[index]?.narx &&
                              errors.yonalishlar?.[index]?.narx
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MoneyIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            name={`yonalishlar.${index}.izoh`}
                            label="Izoh"
                            variant="outlined"
                            error={
                              touched.yonalishlar?.[index]?.izoh &&
                              Boolean(errors.yonalishlar?.[index]?.izoh)
                            }
                            helperText={
                              touched.yonalishlar?.[index]?.izoh &&
                              errors.yonalishlar?.[index]?.izoh
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <NoteIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        pushYonalish({
                          yonalish_id: '',
                          qayerdan: '',
                          qayerga: '',
                          masofa: '',
                          narx: '',
                          izoh: ''
                        })
                      }
                    >
                      Yangi yo'nalish qo'shish
                    </Button>
                  </Box>
                </Box>
              )}
            </FieldArray>
          </StyledPaper>

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {initialValues.id ? 'Yangilash' : 'Saqlash'}
            </Button>
          </Box>

          {error && (
            <Box mt={2} color="error.main">
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default PutyovkaForm;
